/**
 * Vendor console resource — typed wrappers over the `/v1/vendor/*` endpoints
 * (RequireVendorAdmin-gated server-side). The vendor (e.g. Versatile) sees its
 * child foundry orgs + their Versatile-origin equipment slice. Mirrors
 * lib/api/dashboards.ts. See docs/versatile/specs/tenancy-vendor-portal.md §4.6.
 */

import { api } from "@/lib/api-client";
import { fetchAllPages } from "./paginate";

/**
 * A vendor's child foundry org + its equipment / online counts over the slice.
 * Wire shape of internal/vendor/portal.go `CustomerSummary`. The counts key on
 * things.vendor_org_id, so they survive a child's "graduation" to the full twin.
 */
export interface VendorCustomer {
  id: string;
  slug: string;
  name: string;
  created_at: string;
  is_disabled: boolean;
  equipment_count: number;
  online_count: number;
  /** The foundry's real contact email — null until the vendor sets it. Edit via
   *  PATCH /v1/vendor/customers/{org_id}; used to invite the customer's admin. */
  contact_email: string | null;
}

/** Envelope shape (`httpx.List[CustomerSummary]`). */
export interface VendorCustomerList {
  items: VendorCustomer[];
  total: number;
  limit: number;
  offset: number;
}

/** POST /v1/vendor/customers request body (`createCustomerReq`). */
export interface CreateCustomerBody {
  name: string;
  slug?: string;
  admin_email: string;
  provision_sand_plant?: boolean;
}

/**
 * POST /v1/vendor/customers 201 result — the new child foundry org, the
 * passwordless first-admin invite, and its accept link (the onboarding wizard
 * surfaces the link). `org`/`invite` are intentionally loose here; the wizard
 * only needs the link + the org name.
 */
export interface CreateCustomerResult {
  org: { id: string; slug: string; name: string };
  invite: { id: string; email: string; role: string };
  link: string;
  provision_sand_plant_requested: boolean;
  sand_plant_provisioned: boolean;
}

/** Fleet KPI rollup over the equipment slice. Wire shape of vendor.FleetSummary. */
export interface VendorFleetSummary {
  total_equipment: number;
  online: number;
  offline: number;
  gateways: number;
  gateways_online: number;
  instruments: number;
  instruments_online: number;
  customers: number;
  customers_active: number;
  certs_expiring_30d: number;
}

/** One piece of the vendor's equipment + its liveness/health. Wire shape of
 *  internal/vendor/portal.go `EquipmentItem`. hardware_rev is the V-Sync
 *  allow-list revision (legacy); cert_expires_at is the native mTLS leaf. */
export interface VendorEquipment {
  id: string;
  slug: string;
  name: string;
  kind: string;
  product_id: string | null;
  last_seen: string | null;
  online: boolean;
  org_id: string;
  org_name: string;
  hardware_rev: string | null;
  mac: string | null;
  cert_expires_at: string | null;
}

export interface VendorEquipmentList {
  items: VendorEquipment[];
  total: number;
  limit: number;
  offset: number;
}

/** A tag on the vendor's equipment + its latest reading. Wire shape of vendor.VendorTag. */
export interface VendorTag {
  id: string;
  name: string;
  unit: string;
  data_kind: string;
  latest_value: number | null;
  latest_time: string | null;
}

export interface VendorTagList {
  items: VendorTag[];
  total: number;
  /** True when the foundry has hidden its instrument readings from the vendor —
   *  `items` is then empty and the UI shows a "data hidden" note. */
  data_hidden: boolean;
}

/** One point of a tag's value history. Wire shape of vendor.TagValuePoint. */
export interface VendorTagValuePoint {
  t: string;
  v: number;
}

/** A tag's value time-series over a window — drives the per-tag trend chart.
 *  Wire shape of the /tags/{tag_id}/values response. `data_hidden` mirrors the
 *  tags endpoint (foundry hid its readings ⇒ `points` empty). */
export interface VendorTagValues {
  tag_id: string;
  name: string;
  unit: string;
  points: VendorTagValuePoint[];
  data_hidden: boolean;
}

/** One 5-minute bucket of slice throughput. Wire shape of vendor.UsagePoint. */
export interface VendorUsagePoint {
  bucket: string;
  readings: number;
  devices: number;
}

export interface VendorUsageSeries {
  window: string;
  data_through?: string;
  points: VendorUsagePoint[];
}

/** Per-customer slice throughput over the window. Wire shape of vendor.CustomerUsage. */
export interface VendorCustomerUsage {
  org_id: string;
  org_name: string;
  readings: number;
  devices: number;
}

export interface VendorCustomerUsageList {
  items: VendorCustomerUsage[];
  total: number;
}

/** An alarm event on the vendor's equipment. Wire shape of vendor.VendorAlarm. */
export interface VendorAlarm {
  id: string;
  severity: string;
  state: string;
  fired_at: string;
  message: unknown;
  thing_id: string;
  thing_name: string;
  org_id: string;
  org_name: string;
}

export interface VendorAlarmList {
  items: VendorAlarm[];
  total: number;
  limit: number;
  offset: number;
}

/** PATCH /v1/vendor/customers/{org_id} 200 result — the child org id + its
 *  (possibly cleared) contact email. */
export interface PatchCustomerResult {
  id: string;
  contact_email: string | null;
}

/** POST /v1/vendor/customers/{org_id}/{disable|enable} 200 result — the child
 *  org id + its new is_disabled state. */
export interface SetCustomerDisabledResult {
  id: string;
  is_disabled: boolean;
}

/** One pending org invite. Wire shape of orgs.Invite. */
export interface VendorInvite {
  id: string;
  org_id: string;
  email: string;
  role: string;
  status: string;
  created_at: string;
  expires_at: string;
}

/** POST /v1/vendor/customers/{org_id}/invites 201 result — the pending admin
 *  invite + the accept link to send the customer's admin. */
export interface InviteCustomerAdminResult {
  invite: VendorInvite;
  link: string;
}

export const vendor = {
  /** The vendor's child foundry orgs + equipment/online counts. Pages through
   *  ALL of them (the server caps ?limit at 200) so the fleet list stays
   *  complete past 200 customers — a single call would hide everyone after the
   *  first page (the 84-org / 50-row bug). */
  listCustomers: () => fetchAllPages<VendorCustomer>("/v1/vendor/customers"),
  /** Onboard a new foundry under the vendor — creates the child org, stamps the
   *  co-brand origin, and mints the first-admin invite. */
  createCustomer: (body: CreateCustomerBody) =>
    api.post<CreateCustomerResult>("/v1/vendor/customers", body),
  /** Set (or clear, with "") the customer foundry's contact email. 404 if the
   *  org isn't one of this vendor's children. */
  patchCustomer: (orgId: string, contactEmail: string) =>
    api.patch<PatchCustomerResult>(`/v1/vendor/customers/${orgId}`, {
      contact_email: contactEmail,
    }),
  /** Suspend (disabled=true) or restore (false) a customer foundry — flips the
   *  org's is_disabled gate, blocking/allowing its users' sign-in and data
   *  access. 404 if the org isn't one of this vendor's children. */
  setCustomerDisabled: (orgId: string, disabled: boolean) =>
    api.post<SetCustomerDisabledResult>(
      `/v1/vendor/customers/${orgId}/${disabled ? "disable" : "enable"}`,
      {},
    ),
  /** Invite a customer foundry's admin — mints a pending admin invite on the
   *  child org and returns the accept link to send them. 404 if the org isn't
   *  one of this vendor's children. */
  inviteCustomerAdmin: (orgId: string, email: string) =>
    api.post<InviteCustomerAdminResult>(`/v1/vendor/customers/${orgId}/invites`, {
      email,
    }),
  /** Fleet KPI rollup over the equipment slice (online/gateways/instruments/certs). */
  fleetOverview: () => api.get<VendorFleetSummary>("/v1/vendor/fleet/overview"),
  /** Flat cross-customer equipment list (the fleet table). Pages through ALL
   *  rows (the server caps ?limit at 200) so the fleet's device table isn't
   *  silently truncated to the first 50 once the fleet grows past a page — the
   *  same 50-row cap that hid customers past the first page. */
  listEquipment: () => fetchAllPages<VendorEquipment>("/v1/vendor/equipment"),
  /** ONE customer's full equipment slice (uncapped) — the per-customer endpoint.
   *  The customer-detail page must use this, not the page-capped flat list above,
   *  or customers beyond the global first-50 render as empty. */
  customerEquipment: (orgId: string) =>
    api.get<VendorEquipmentList>(`/v1/vendor/customers/${orgId}/equipment`),
  /** A device's tags + latest readings — the equipment-scoped drill-in.
   *  `data_hidden` ⇒ the foundry hid its readings (items empty). */
  equipmentTags: (orgId: string, thingId: string) =>
    api.get<VendorTagList>(`/v1/vendor/customers/${orgId}/equipment/${thingId}/tags`),
  /** One tag's value history (the per-tag trend). Defaults to the last 7 days
   *  server-side; pass RFC3339 from/to to window it, and limit to cap points.
   *  `data_hidden` ⇒ the foundry hid its readings (points empty). */
  equipmentTagValues: (
    orgId: string,
    thingId: string,
    tagId: string,
    params?: { from?: string; to?: string; limit?: number },
  ) => {
    const q = new URLSearchParams();
    if (params?.from) q.set("from", params.from);
    if (params?.to) q.set("to", params.to);
    if (params?.limit != null) q.set("limit", String(params.limit));
    const qs = q.toString();
    return api.get<VendorTagValues>(
      `/v1/vendor/customers/${orgId}/equipment/${thingId}/tags/${tagId}/values${qs ? `?${qs}` : ""}`,
    );
  },
  /** Slice throughput time-series (usage). window = 5m|1h|1d|7d. */
  usageIngestRate: (window = "1d") =>
    api.get<VendorUsageSeries>(`/v1/vendor/usage/ingest-rate?window=${window}`),
  /** Per-customer slice throughput (usage by foundry). */
  usageByCustomer: (window = "1d") =>
    api.get<VendorCustomerUsageList>(`/v1/vendor/usage/by-customer?window=${window}`),
  /** Cross-customer alarm feed on the vendor's equipment. */
  fleetAlarms: (state?: string) =>
    api.get<VendorAlarmList>(`/v1/vendor/alarms${state ? `?state=${state}` : ""}`),
};
